import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@fly/core';
import { closeDialog, openDialog, useAppDispatch } from '@fly/store';

export const useRevokeTokenDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const openRevokeTokenDialog = useCallback(
    (tokenName: string, onSubmit: () => Promise<unknown>) => {
      const handleSubmit = async () => {
        setIsLoading(true);
        try {
          await onSubmit();
          dispatch(closeDialog());
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          setIsLoading(false);
        }
      };

      dispatch(
        openDialog({
          title: t(
            'app.dashboard.token_management.messages.revoke_confirmation',
            {
              defaultValue:
                'Are you sure you want to revoke the token {{tokenName}}?',
              tokenName,
            }
          ),
          content: (
            <div data-testid='token-revoke-dialog'>
              <Text as='p' size='3' highContrast className='mb-fly-6'>
                {t(
                  'app.dashboard.token_management.messages.revoke_confirmation_description',
                  {
                    defaultValue:
                      'This action is irreversible and will affect everything that relies on this token.',
                  }
                )}
              </Text>
              <div className='flex flex-row justify-end gap-fly-3'>
                <Button
                  data-testid='token-revoke-dialog-cancel-btn'
                  variant='soft'
                  size='2'
                  color='neutral'
                  onClick={() => dispatch(closeDialog())}
                  disabled={isLoading}
                >
                  {t('app.dashboard.token_management.buttons.cancel', {
                    defaultValue: 'Cancel',
                  })}
                </Button>
                <Button
                  data-testid='token-revoke-dialog-revoke-btn'
                  variant='soft'
                  size='2'
                  color='error'
                  onClick={handleSubmit}
                  loading={isLoading}
                >
                  {t('app.dashboard.token_management.buttons.revoke', {
                    defaultValue: 'Revoke',
                  })}
                </Button>
              </div>
            </div>
          ),
        })
      );
    },
    [dispatch, t, isLoading]
  );

  return { openRevokeTokenDialog };
};
